import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';

import { signInWithEmailAndPassword } from 'firebase/auth';

import PasswordField from '../../Components/PasswordField';
import { USER_TYPES } from '../../Constant/Enums';
import { prepareFirebaseAuth } from '../../lib/firebaseClients';

const FirebaseToken = () => {
  const [userType, setUserType] = useState(USER_TYPES.ADMIN);
  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleChange = event => {
    const { name, value } = event.target;
    setInputs(current => ({ ...current, [name]: value }));
  };

  const handleUserTypeChange = (_, nextUserType) => {
    if (!nextUserType) return;
    setUserType(nextUserType);
    setToken('');
    setError('');
    setIsCopied(false);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setToken('');
    setIsCopied(false);
    setIsLoading(true);

    try {
      const auth = await prepareFirebaseAuth(userType);
      const response = await signInWithEmailAndPassword(
        auth,
        inputs.email.trim(),
        inputs.password
      );
      const idToken = await response.user.getIdToken(true);

      setToken(idToken);
    } catch (error) {
      setError(error.code || error.message || 'Firebase login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setIsCopied(true);
  };

  return (
    <Box
      minHeight='100vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
      bgcolor='#f6f8fb'
      px={2}
      py={4}
    >
      <Paper
        component='form'
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 680,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 2.5, sm: 4 },
        }}
      >
        <Stack gap={3}>
          <Stack direction='row' alignItems='center' gap={1.5}>
            <KeyIcon color='primary' />
            <Typography variant='h5' fontWeight={700}>
              Firebase Token
            </Typography>
          </Stack>

          <ToggleButtonGroup
            exclusive
            fullWidth
            color='primary'
            value={userType}
            onChange={handleUserTypeChange}
          >
            <ToggleButton value={USER_TYPES.ADMIN}>Admin</ToggleButton>
            <ToggleButton value={USER_TYPES.USER}>User</ToggleButton>
            <ToggleButton value={USER_TYPES.PROFESSIONAL}>Professional</ToggleButton>
          </ToggleButtonGroup>

          <FormControl fullWidth>
            <FormLabel>Email</FormLabel>
            <TextField
              required
              type='email'
              name='email'
              autoComplete='email'
              value={inputs.email}
              onChange={handleChange}
              placeholder='name@example.com'
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>Password</FormLabel>
            <PasswordField
              required
              name='password'
              autoComplete='current-password'
              value={inputs.password}
              onChange={handleChange}
              placeholder='Password'
            />
          </FormControl>

          {error && <Alert severity='error'>{error}</Alert>}

          <Button
            type='submit'
            variant='contained'
            size='large'
            disabled={isLoading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {isLoading ? 'Getting token...' : 'Get Token'}
          </Button>

          {token && (
            <Stack gap={1.5}>
              <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
                <Typography fontWeight={700}>ID Token</Typography>
                <Button
                  type='button'
                  size='small'
                  variant='outlined'
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                >
                  {isCopied ? 'Copied' : 'Copy'}
                </Button>
              </Stack>

              <TextField
                multiline
                minRows={8}
                value={token}
                InputProps={{ readOnly: true }}
              />
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default FirebaseToken;
